import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { InterviewChat } from '../../components/chat/InterviewChat';
import { chatService } from '../../services/chat';
import {
  InterviewSession,
  InterviewType,
  ExperienceLevel,
  StartInterviewRequest,
} from '../../types/chat';
import { SEO } from '../../components/SEO';
import { AdBanner } from '../../components/ads/AdBanner';
import { analytics } from '../../services/analytics';
import { EventActions, EventCategories } from '../../config/analytics';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Button } from '../../components/ui/Button';
import { Modal } from '../../components/ui/Modal';

const GUEST_SESSION_KEY = 'interviewr.guest.session';

type GuestSessionPayload = {
  session: InterviewSession;
  config: {
    interviewType: InterviewType;
    jobRole: string;
    experienceLevel: ExperienceLevel;
  };
  savedAt: number;
};

function persistGuestSession(payload: GuestSessionPayload | null) {
  try {
    if (payload === null) {
      localStorage.removeItem(GUEST_SESSION_KEY);
    } else {
      localStorage.setItem(GUEST_SESSION_KEY, JSON.stringify(payload));
    }
  } catch {
    /* localStorage may be unavailable (private mode); fail soft */
  }
}

const interviewTypeOptions: { value: InterviewType; label: string; desc: string }[] = [
  { value: InterviewType.General, label: 'General', desc: 'Common interview questions' },
  { value: InterviewType.Behavioral, label: 'Behavioral', desc: 'STAR method practice' },
  { value: InterviewType.Technical, label: 'Technical', desc: 'Coding & problem-solving' },
  { value: InterviewType.SystemDesign, label: 'System Design', desc: 'Architecture questions' },
];

const experienceOptions: { value: ExperienceLevel; label: string }[] = [
  { value: ExperienceLevel.EntryLevel, label: 'Entry level' },
  { value: ExperienceLevel.MidLevel, label: 'Mid level' },
  { value: ExperienceLevel.Senior, label: 'Senior' },
];

const seo = (
  <SEO
    title="Free AI Mock Interview Demo | InterviewR"
    description="Practice a short AI mock interview for free — no account required. Get instant feedback, then sign up for unlimited sessions and real interviewers."
    keywords="free mock interview, AI interview practice, interview preparation online"
    url={
      typeof window !== 'undefined' ? window.location.href : 'https://interview-r.com/try-interview'
    }
  />
);

export const TryInterview: React.FC = () => {
  const [session, setSession] = useState<InterviewSession | null>(null);
  const [interviewType, setInterviewType] = useState<InterviewType>(InterviewType.General);
  const [jobRole, setJobRole] = useState('Software Engineer');
  const [experienceLevel, setExperienceLevel] = useState<ExperienceLevel>(ExperienceLevel.MidLevel);
  const [startError, setStartError] = useState<string | null>(null);
  const [starting, setStarting] = useState(false);
  const [quotaDone, setQuotaDone] = useState(false);

  const startDemo = async () => {
    setStartError(null);
    setStarting(true);
    try {
      const body: StartInterviewRequest = {
        interviewType,
        jobRole: jobRole.trim() || 'Software Engineer',
        companyName: undefined,
        experienceLevel,
      };
      const s = await chatService.startGuestInterview(body);
      setSession(s);
      setQuotaDone(false);
      persistGuestSession({
        session: s,
        config: { interviewType, jobRole: body.jobRole ?? 'Software Engineer', experienceLevel },
        savedAt: Date.now(),
      });
      analytics.trackConversion(EventActions.GUEST_DEMO_START);
    } catch (e) {
      if (axios.isAxiosError(e) && e.response?.status === 409) {
        const msg = (e.response.data as { message?: string })?.message;
        setStartError(
          msg || 'You already used your free try today. Create a free account to keep practicing.',
        );
      } else {
        setStartError('Could not start the demo. Please try again later.');
      }
    } finally {
      setStarting(false);
    }
  };

  const handleSessionUpdate = (next: InterviewSession) => {
    setSession(next);
    persistGuestSession({
      session: next,
      config: { interviewType, jobRole: jobRole.trim() || 'Software Engineer', experienceLevel },
      savedAt: Date.now(),
    });
  };

  const handleQuotaExhausted = () => {
    setQuotaDone(true);
    analytics.trackConversion(EventActions.GUEST_DEMO_QUOTA_HIT);
  };

  const handleSessionEnd = () => {
    setSession(null);
    setQuotaDone(false);
    persistGuestSession(null);
  };

  const trackUpsellClick = () => {
    analytics.trackConversion(EventActions.GUEST_SIGNUP_CTA_CLICK);
  };

  useEffect(() => {
    if (!quotaDone) return;
    analytics.trackEvent({
      category: EventCategories.CONVERSION,
      action: EventActions.GUEST_QUOTA_MODAL_VIEW,
      label: 'try-interview',
    });
  }, [quotaDone]);

  if (session) {
    return (
      <>
        {seo}
        <div className="min-h-[calc(100dvh-8rem)] bg-stitch-canvas px-4 py-6 sm:px-6 lg:px-8">
          <div className="mx-auto grid min-h-0 max-w-7xl grid-cols-1 gap-6 lg:min-h-[calc(100dvh-8rem)] lg:grid-cols-12 lg:gap-8 lg:items-stretch">
            <div className="flex min-h-0 flex-col gap-6 lg:col-span-8">
              <div className="flex h-[calc(100dvh-7rem)] flex-col overflow-hidden rounded-2xl border border-stitch-border bg-stitch-surface shadow-stitch-card lg:h-auto lg:min-h-[min(820px,calc(100dvh-7rem))] lg:flex-1">
                <div className="min-h-0 flex-1">
                  <InterviewChat
                    session={session}
                    guestMode
                    onGuestQuotaExhausted={handleQuotaExhausted}
                    onSessionUpdate={handleSessionUpdate}
                    onSessionEnd={handleSessionEnd}
                  />
                </div>
              </div>
              <AdBanner slot="chat-bottom" />
            </div>
            <aside className="flex flex-col gap-6 lg:col-span-4">
              <div className="rounded-stitch-xl border border-stitch-border bg-stitch-surface p-6 shadow-stitch-card">
                <h3 className="mb-2 flex items-center gap-2 font-semibold text-stitch-fg">
                  <span className="material-symbols-outlined text-stitch-primary">tips_and_updates</span>
                  Session tips
                </h3>
                <ul className="space-y-2 text-sm text-stitch-muted">
                  <li>Speak clearly — the AI adapts to your answers.</li>
                  <li>Use the STAR method for behavioral prompts.</li>
                  <li>End anytime; your progress is saved when you sign up.</li>
                </ul>
              </div>
            </aside>
          </div>
        </div>
        <Modal open={quotaDone} onClose={() => setQuotaDone(false)} size="md">
          <div className="text-center">
            <span className="mx-auto mb-3 inline-flex h-14 w-14 items-center justify-center rounded-full bg-stitch-primary-soft text-stitch-primary">
              <span className="material-symbols-outlined text-3xl">military_tech</span>
            </span>
            <h2 className="text-xl font-bold text-stitch-fg">Nice work — demo complete</h2>
            <p className="mt-2 text-sm text-stitch-muted">
              Create a free account to keep going where you left off, save scoring history, and unlock unlimited AI sessions.
            </p>
            <div className="mt-6 flex flex-col gap-2">
              <Link
                to="/register"
                onClick={trackUpsellClick}
                className="inline-flex w-full items-center justify-center rounded-stitch-xl bg-stitch-primary px-5 py-3 text-sm font-semibold text-white transition hover:bg-stitch-primary-hover"
              >
                Create free account
                <span className="material-symbols-outlined ml-1 text-base">arrow_forward</span>
              </Link>
              <Link
                to="/interviewers"
                onClick={trackUpsellClick}
                className="inline-flex w-full items-center justify-center rounded-stitch-xl border border-stitch-border bg-stitch-surface px-5 py-3 text-sm font-semibold text-stitch-fg transition hover:border-stitch-primary hover:text-stitch-primary"
              >
                Or book a real expert
              </Link>
              <button
                type="button"
                onClick={() => setQuotaDone(false)}
                className="mt-1 text-xs text-stitch-muted hover:text-stitch-fg"
              >
                Keep reviewing the demo
              </button>
            </div>
          </div>
        </Modal>
      </>
    );
  }

  return (
    <>
      {seo}
      <div className="min-h-screen bg-stitch-canvas px-4 py-8 sm:px-6 lg:px-8">
        <div className="mx-auto grid max-w-7xl grid-cols-1 items-start gap-8 lg:grid-cols-12">
          <div className="flex flex-col gap-6 lg:col-span-8">
            <AdBanner slot="try-interview-top" />

            <div className="rounded-stitch-2xl border border-stitch-border bg-stitch-surface shadow-stitch-card">
              <div className="border-b border-stitch-border bg-stitch-surface-2/80 px-6 py-5">
                <div className="flex flex-wrap items-center justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-stitch-primary-soft text-stitch-primary">
                      <span className="material-symbols-outlined">smart_toy</span>
                    </div>
                    <div>
                      <h1 className="text-lg font-semibold text-stitch-fg">AI Mock Interview</h1>
                      <p className="text-xs text-stitch-muted">No sign-up required for the free demo</p>
                    </div>
                  </div>
                  <span className="rounded-full border border-stitch-border bg-stitch-surface px-3 py-1 text-xs font-medium text-stitch-muted">
                    Guest demo
                  </span>
                </div>
              </div>

              <div className="space-y-6 p-6 sm:p-8">
                {startError && (
                  <div className="rounded-stitch-lg border border-stitch-warning/40 bg-stitch-warning-soft px-4 py-3 text-sm text-stitch-fg">
                    {startError}{' '}
                    <Link to="/register" className="font-semibold text-stitch-primary underline">
                      Sign up free
                    </Link>
                  </div>
                )}

                <div>
                  <span className="mb-2 block text-sm font-semibold text-stitch-fg-soft">Interview type</span>
                  <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                    {interviewTypeOptions.map((o) => (
                      <button
                        key={o.value}
                        type="button"
                        onClick={() => setInterviewType(o.value)}
                        className={`rounded-stitch-xl border-2 p-3 text-left transition-all ${
                          interviewType === o.value
                            ? 'border-stitch-primary bg-stitch-primary-soft shadow-stitch-card'
                            : 'border-stitch-border bg-stitch-surface hover:border-stitch-muted'
                        }`}
                      >
                        <span
                          className={`block text-sm font-semibold ${
                            interviewType === o.value ? 'text-stitch-primary' : 'text-stitch-fg'
                          }`}
                        >
                          {o.label}
                        </span>
                        <span className="mt-0.5 block text-xs text-stitch-muted">{o.desc}</span>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="grid gap-4 sm:grid-cols-2">
                  <Input
                    label="Target role"
                    type="text"
                    value={jobRole}
                    onChange={(e) => setJobRole(e.target.value)}
                    placeholder="e.g. Product Manager"
                  />
                  <Select
                    label="Experience level"
                    value={String(experienceLevel)}
                    onChange={(e) => setExperienceLevel(Number(e.target.value) as ExperienceLevel)}
                  >
                    {experienceOptions.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </Select>
                </div>

                <Button
                  type="button"
                  variant="primary"
                  size="lg"
                  onClick={startDemo}
                  disabled={starting}
                  className="w-full sm:w-auto"
                >
                  <span className="material-symbols-outlined text-[22px]">play_arrow</span>
                  {starting ? 'Starting...' : 'Start free interview'}
                </Button>

                <p className="text-xs text-stitch-muted">
                  One demo session per day. By trying the demo you agree to our{' '}
                  <Link to="/terms" className="underline hover:text-stitch-fg-soft">
                    Terms
                  </Link>{' '}
                  and{' '}
                  <Link to="/privacy-policy" className="underline hover:text-stitch-fg-soft">
                    Privacy Policy
                  </Link>
                  .
                </p>
              </div>
            </div>

            <div className="grid gap-6 pb-8 text-center sm:grid-cols-3">
              {[
                { title: 'Instant feedback', desc: 'AI evaluates your answers in real time', icon: 'bolt' },
                { title: 'Multiple formats', desc: 'Behavioral, Technical, System Design & more', icon: 'category' },
                { title: 'Totally free', desc: 'No account or payment needed to try', icon: 'verified' },
              ].map((b) => (
                <div key={b.title} className="flex flex-col items-center rounded-stitch-xl border border-stitch-border bg-stitch-surface p-4 shadow-stitch-card">
                  <span className="material-symbols-outlined mb-2 text-3xl text-stitch-success">{b.icon}</span>
                  <h3 className="text-sm font-semibold text-stitch-fg">{b.title}</h3>
                  <p className="mt-1 text-xs text-stitch-muted">{b.desc}</p>
                </div>
              ))}
            </div>
          </div>

          <aside className="flex flex-col gap-6 lg:col-span-4">
            <div className="rounded-stitch-xl border border-stitch-border bg-stitch-surface p-6 shadow-stitch-card">
              <h2 className="mb-3 flex items-center gap-2 font-semibold text-stitch-fg">
                <span className="material-symbols-outlined text-stitch-accent">auto_awesome</span>
                Why try the demo?
              </h2>
              <p className="text-sm leading-relaxed text-stitch-muted">
                Experience how InterviewR structures a realistic interview flow before you create an account.
              </p>
              <ul className="mt-4 space-y-2 text-sm text-stitch-fg-soft">
                <li className="flex gap-2">
                  <span className="material-symbols-outlined text-stitch-success text-lg">check_circle</span>
                  Real-time AI responses
                </li>
                <li className="flex gap-2">
                  <span className="material-symbols-outlined text-stitch-success text-lg">check_circle</span>
                  No payment required
                </li>
                <li className="flex gap-2">
                  <span className="material-symbols-outlined text-stitch-success text-lg">check_circle</span>
                  Upgrade anytime for experts
                </li>
              </ul>
            </div>
            <p className="text-center text-sm text-stitch-muted lg:text-left">
              Already have an account?{' '}
              <Link to="/login" className="font-medium text-stitch-primary hover:underline">
                Sign in
              </Link>{' '}
              for full AI training and bookings.
            </p>
          </aside>
        </div>
      </div>
    </>
  );
};
